/**
 * SMS channel — per-page components for the domain-app shell.
 * Each page wires the useSmsCommunications hook to the relevant prop-driven tab.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconLayoutDashboard,
  IconSend,
  IconTemplate,
  IconPencil,
  IconUsersGroup,
  IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { Group, Loader } from '../../../../components/ui';
import { ChannelPage } from '../_shared/ChannelPage';
import { ChannelDashboard } from '../_shared/ChannelDashboard';
import { ProviderSettingsForm } from '../_shared/ProviderSettingsForm';
import { BulkSendWizard } from '../_shared/BulkSendWizard';
import { toInternational } from '../_shared/phone';
import { CampaignsList, CampaignDetail } from '../_shared/CampaignsView';
import { useSmsCommunications } from './hooks/useSmsCommunications';
import {
  SmsQueueTab,
  SmsTemplatesTab,
  SmsTestTab,
  SmsQueueModal,
  SmsTemplateModal,
} from './components';
import type { AppSMSModelView, AppSMSTemplateModelView, SmsTestData } from './types';

export function SmsDashboardPage() {
  return (
    <ChannelPage
      icon={<IconLayoutDashboard size={28} />}
      title="SMS"
      subtitle="Overview of the SMS channel. Use the left menu to compose, review the outbox, manage templates, and configure the channel."
    >
      <ChannelDashboard channel="sms" />
    </ChannelPage>
  );
}

export function SmsComposePage() {
  const { loading, templates, fetchTemplates, createSms } = useSmsCommunications();

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSend = async (data: SmsTestData) => {
    const smsData = {
      toNumber: toInternational(data.cellNumber),
      fromNumber: 'CCMS',
      message: data.message,
      date: new Date().toISOString(),
      priority: data.priority,
      scheduledDateTime: data.scheduledDateTime || null,
      status: 'Pending',
      templateID: data.templateId || null,
      isSent: false,
      retryCount: 0,
      maxRetryCount: 3,
    };
    await createSms(smsData as any);
  };

  return (
    <ChannelPage icon={<IconPencil size={28} />} title="Compose" subtitle="Compose and queue an SMS.">
      <SmsTestTab templates={templates} loading={loading} onSend={handleSend} />
    </ChannelPage>
  );
}

export function SmsBulkSendPage() {
  return (
    <ChannelPage icon={<IconUsersGroup size={28} />} title="Bulk Send" subtitle="Send to many recipients via a campaign.">
      <BulkSendWizard channel="sms" />
    </ChannelPage>
  );
}

export function SmsCampaignsPage() {
  const navigate = useNavigate();
  return (
    <ChannelPage icon={<IconSpeakerphone size={28} />} title="Campaigns" subtitle="Bulk campaign history with delivery counts and per-recipient status.">
      <CampaignsList channel="sms" onOpen={(id) => navigate(`/admin/communications/sms/campaigns/${id}`)} />
    </ChannelPage>
  );
}

export function SmsCampaignDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  return (
    <ChannelPage
      icon={<IconSpeakerphone size={28} />}
      title="Campaigns"
      subtitle="Delivery counts and per-recipient status."
      onBack={() => navigate('/admin/communications/sms/campaigns')}
    >
      <CampaignDetail campaignId={Number(id)} />
    </ChannelPage>
  );
}

export function SmsOutboxPage() {
  const {
    loading,
    queueItems,
    fetchQueue,
    deleteQueueItem,
    bulkDeleteQueueItems,
    retryFailedItem,
    updatePriority,
    updateQueueItem,
  } = useSmsCommunications();

  const [modalOpened, setModalOpened] = useState(false);
  const [selected, setSelected] = useState<AppSMSModelView | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  return (
    <ChannelPage icon={<IconSend size={28} />} title="Outbox" subtitle="Queued & sent SMS with delivery status.">
      {loading && queueItems.length === 0 ? (
        <Group justify="center" p="xl">
          <Loader size="lg" />
        </Group>
      ) : (
        <SmsQueueTab
          queueItems={queueItems}
          loading={loading}
          onRefresh={fetchQueue}
          onView={(item: AppSMSModelView) => { setSelected(item); setMode('view'); setModalOpened(true); }}
          onEdit={(item: AppSMSModelView) => { setSelected(item); setMode('edit'); setModalOpened(true); }}
          onDelete={deleteQueueItem}
          onBulkDelete={bulkDeleteQueueItems}
          onRetry={retryFailedItem}
          onUpdatePriority={updatePriority}
        />
      )}
      <SmsQueueModal
        opened={modalOpened}
        onClose={() => { setModalOpened(false); setSelected(null); }}
        item={selected}
        mode={mode}
        onSave={async (data: Partial<AppSMSModelView>) => {
          if (selected) {
            await updateQueueItem(selected.messageID, data as any);
            setModalOpened(false);
            setSelected(null);
          }
        }}
        loading={loading}
      />
    </ChannelPage>
  );
}

export function SmsTemplatesPage() {
  const {
    loading,
    templates,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useSmsCommunications();

  const [modalOpened, setModalOpened] = useState(false);
  const [selected, setSelected] = useState<AppSMSTemplateModelView | null>(null);
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>('view');

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <ChannelPage icon={<IconTemplate size={28} />} title="Templates" subtitle="Reusable SMS templates.">
      {loading && templates.length === 0 ? (
        <Group justify="center" p="xl">
          <Loader size="lg" />
        </Group>
      ) : (
        <SmsTemplatesTab
          templates={templates}
          loading={loading}
          onRefresh={fetchTemplates}
          onView={(t: AppSMSTemplateModelView) => { setSelected(t); setMode('view'); setModalOpened(true); }}
          onEdit={(t: AppSMSTemplateModelView) => { setSelected(t); setMode('edit'); setModalOpened(true); }}
          onDelete={deleteTemplate}
          onCreate={() => { setSelected(null); setMode('create'); setModalOpened(true); }}
        />
      )}
      <SmsTemplateModal
        opened={modalOpened}
        onClose={() => { setModalOpened(false); setSelected(null); }}
        template={selected}
        mode={mode}
        onSave={async (data: Partial<AppSMSTemplateModelView>) => {
          if (mode === 'create') {
            await createTemplate(data);
          } else if (selected) {
            await updateTemplate(selected.templateID, data);
          }
          setModalOpened(false);
          setSelected(null);
        }}
        loading={loading}
      />
    </ChannelPage>
  );
}

export function SmsSettingsPage() {
  return (
    <ChannelPage icon={<IconSettings size={28} />} title="Settings" subtitle="Provider selection (AWS / additional provider) and channel configuration.">
      <ProviderSettingsForm channel="sms" />
    </ChannelPage>
  );
}
